<template>
  <DashboardPanelTemplate :panelSize="panelSize" :panelFocus="panelFocus"
    background="mlm-shipping-bottom-right" :loading="loading">
    <h4>{{$t('Header')}}</h4>
    <p>{{$t('SubHeader')}}</p>
    <div class="panel-wrapper">
      <div class='get-started-content'>
        <div
          class='checkList'
          v-for='(step, index) of getStartedSteps'
          :key='index'
          v-bind:id="$t(getDivId(step))"
          @click.prevent="clickItem(step.ItemId, step.IsCompleted, step.Href, step.IsLegacyHref)">
          <circle-checkmark-icon v-if="step.IsCompleted" variation="complete" />
          <div v-else class="not-cmp"></div>

          <i18n tag="p" :path="getContent(step)"
            :class="{'active': activeStep === step.ItemId}"
            class="checkList">
            <span place="time"
              class="font-italic"
              v-if="activeStep !== step.ItemId">
              {{$t(getTime(step))}}
            </span>
            <span place="time" v-else>
              {{$t(getTime(step))}}
            </span>
          </i18n>
        </div>
      </div>
      <p class="footer">
        {{$t('Footer')}}
      </p>
    </div>
    <clear-demo-data-modal
      v-if="showClearDemoDataModal"
      @close-modal="setShowClearDemoDataModal(false)"
      @modal-submitted="clearDemoDataModalSubmitted()">
    </clear-demo-data-modal>
    <additional-questions-modal
      v-if="showAdditionalQuestionsModal"
      @close-modal="setShowAdditionalQuestionsModal(false)"
      @modal-submitted="additionalQuestionsModalSubmitted()">
    </additional-questions-modal>
  </DashboardPanelTemplate>
</template>
<script>
import TickMark from '@/components/dashboard/utils/TickMark'
import DashboardPanelTemplate from '@/components/layouts/DashboardPanelTemplate'
import GetStartedStepsEnum from '@/enums/modules/setup/getStartedSteps'
import ClearDemoDataModal from '@/components/dashboard/modals/ClearDemoDataModal'
import AdditionalQuestionsModal from '@/components/dashboard/modals/AdditionalQuestionsModal'
import BaseRoutesEnum from '@/enums/modules/routes/base'
import { SetupGuideEnum } from '@/enums/modules/routes'

export default {
  name: 'GetStartedSetupPanel',
  props: {
    panelSize: {
      type: String,
      required: true
    },
    panelFocus: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    TickMark,
    DashboardPanelTemplate,
    ClearDemoDataModal,
    AdditionalQuestionsModal
  },
  data () {
    return {
      showClearDemoDataModal: false,
      showAdditionalQuestionsModal: false
    }
  },
  created () {
    this.$shipBobStore.dispatch('setupDashboard/getGetStartedSteps')
  },
  computed: {
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    impersonatedUserId () {
      return this.user.impersonatedUserId
    },
    storageIdentifier () {
      return `getStartedSteps-${this.impersonatedUserId}`
    },
    getStartedSteps () {
      return this.$shipBobStore.getters['setupDashboard/getStartedSteps']
    },
    activeStep () {
      const activeStep = this.getStartedSteps.find(x => !x.IsCompleted)
      if (activeStep) {
        return activeStep.ItemId
      } else {
        // If no steps is pending then run the Handle onboarding complete
        this.handleOnboardingComplete()
        // Delete the localStorage that is no longer needed.
        localStorage.removeItem(this.storageIdentifier)
        return 0
      }
    }
  },
  methods: {
    getContent (step) {
      return 'Step' + step.ItemId + '.Content'
    },
    getTime (step) {
      return 'Step' + step.ItemId + '.Time'
    },
    getDivId (step) {
      return 'Step' + step.ItemId + '.DivId'
    },
    clickItem (itemId, isCompleted, Href, IsLegacyHref) {
      if (itemId === GetStartedStepsEnum.TakeInteractiveTour) {
        if (!isCompleted) {
          this.dispatchStoreStepComplete(itemId)
        }
      } else if (itemId === GetStartedStepsEnum.UnderstandPricingPlan) {
        if (!isCompleted) {
          this.dispatchStoreStepComplete(itemId)
        }
      } else if (itemId === GetStartedStepsEnum.ClearDemoData) {
        this.setShowClearDemoDataModal(true)
      }

      if (Href) {
        if (IsLegacyHref) {
          window.location.href = Href
        } else {
          this.$router.push({ path: Href })
        }
      }
    },
    setShowClearDemoDataModal (value) {
      this.showClearDemoDataModal = value
    },
    clearDemoDataModalSubmitted () {
      this.setShowClearDemoDataModal(false)
    },
    setShowAdditionalQuestionsModal (value) {
      this.showAdditionalQuestionsModal = value
    },
    additionalQuestionsModalSubmitted () {
      this.dispatchStoreStepComplete(GetStartedStepsEnum.CertifyFulfillmentSettings)
      this.setShowAdditionalQuestionsModal(false)
    },
    dispatchStoreStepComplete (ItemId) {
      this.$shipBobStore.dispatch('setupDashboard/setGetStartedStep', {ItemId, IsCompleted: true})
    },
    handleOnboardingComplete () {
      const user = this.$shipBobStore.getters['user/getUser']
      this.$shipBobStore.dispatch('user/setUserStep', user.getStepEnum(SetupGuideEnum.OnboardingComplete)).then((response) => {
        this.$router.push({ name: BaseRoutesEnum.Dashboard })
      })
    }
  },
  i18n: {
    messages: {
      'en-US': {
        Header: `Welcome Aboard the 'Ship!`,
        SubHeader: `Let's get you set up. Follow these steps to get started:`,
        Footer: `Once we receive your inventory, you can start shipping your orders!`,
        ['Step' + GetStartedStepsEnum.TakeInteractiveTour]: {
          Content: `Take interactive tour {time}`,
          Time: '(2 mins)',
          DivId: 'launchInteractiveTour'
        },
        ['Step' + GetStartedStepsEnum.UnderstandPricingPlan]: {
          Content: `Understand Pricing + SLAs {time}`,
          Time: '(2 mins)'
        },
        ['Step' + GetStartedStepsEnum.ClearDemoData]: {
          Content: `Clear demo data from your account {time}`,
          Time: '(1 min)'
        },
        ['Step' + GetStartedStepsEnum.StoreIntegration]: {
          Content: `Integrate your store(s) {time}`,
          Time: '(3 mins)'
        },
        ['Step' + GetStartedStepsEnum.ConfigureProductPreference]: {
          Content: `Configure your product preferences {time}`,
          Time: '(5 mins)'
        },
        ['Step' + GetStartedStepsEnum.ConfigureShippingPreference]: {
          Content: `Configure your shipping preferences {time}`,
          Time: '(5 mins)'
        },
        ['Step' + GetStartedStepsEnum.SendInventory]: {
          Content: `Send us your inventory {time}`,
          Time: '(5 mins)'
        },
        ['Step' + GetStartedStepsEnum.ConfigureBilling]: {
          Content: `Add Billing Information {time}`,
          Time: '(1 min)'
        }
      }
    }
  }
}

</script>
<style scoped lang='scss'>
@import '@/../assets/scss/shipbob-custom/components/dashboard/_setup-panel-get-started.scss';
</style>
